import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { useSound } from '@/context/SoundContext';
import { TommyBackground } from '@/components/TommyBackground';
import { useTranslation } from 'react-i18next';
import { 
  ArrowRight, Bot, Shield, Zap, 
  Video, Image, Mic, FileText, Upload, MessagesSquare, Brain,
  DollarSign, Clock, Globe, RefreshCw, Lock, Users, Play, Code, Database,
  Sparkles, ChevronDown, Volume2, VolumeX, Check, Star, Gift
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Premium Tommy images - Diamond Forehead Version
const TOMMY_HERO = "https://static.prod-images.emergentagent.com/jobs/5a9b2831-6b5b-4cfc-b7a9-9566f26820d4/images/c7da3216059a11860fe83b1e985780368f061d4f2d853eeb3cc8524c64435033.png";
const TOMMY_MASCOT = "https://static.prod-images.emergentagent.com/jobs/5a9b2831-6b5b-4cfc-b7a9-9566f26820d4/images/8a5b3d0ce503ee6e5d109b6acca877668382c804aa320000e401fda7fbbae6b4.png";

// Typewriter effect component
function TypewriterText({ text, className, delay = 0, onComplete }) {
  const [displayedText, setDisplayedText] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isComplete, setIsComplete] = useState(false);

  useEffect(() => {
    if (currentIndex === 0) {
      const startDelay = setTimeout(() => {
        setCurrentIndex(1);
      }, delay);
      return () => clearTimeout(startDelay);
    }

    if (currentIndex <= text.length) {
      const timeout = setTimeout(() => {
        setDisplayedText(text.slice(0, currentIndex));
        setCurrentIndex(currentIndex + 1);
      }, 50); // Speed of typing
      return () => clearTimeout(timeout);
    } else if (!isComplete) {
      setIsComplete(true);
      onComplete?.();
    }
  }, [currentIndex, text, delay, isComplete, onComplete]);

  return (
    <span className={className}>
      {displayedText}
      {!isComplete && (
        <motion.span
          animate={{ opacity: [1, 0] }}
          transition={{ duration: 0.5, repeat: Infinity }}
          className="inline-block w-[3px] h-[1em] bg-current ml-1 align-middle"
        />
      )}
    </span>
  );
}

// Animated particle component
function Particles() {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {[...Array(30)].map((_, i) => (
        <motion.div
          key={i}
          className="absolute w-1 h-1 rounded-full"
          style={{
            background: i % 3 === 0 ? '#14b8a6' : i % 3 === 1 ? '#22c55e' : '#2563eb',
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
          }}
          animate={{
            y: [0, -30, 0],
            opacity: [0.2, 0.8, 0.2],
            scale: [1, 1.5, 1],
          }}
          transition={{
            duration: 3 + Math.random() * 2,
            repeat: Infinity,
            delay: Math.random() * 2,
          }}
        />
      ))}
    </div>
  );
}

// Interactive card with sound - ENHANCED with better feedback
function InteractiveCard({ children, className = '', delay = 0, onClick, soundType = 'hover' }) {
  const { playSound } = useSound();
  const [isHovered, setIsHovered] = useState(false);
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      whileHover={{ y: -8, scale: 1.02, transition: { duration: 0.3 } }}
      onHoverStart={() => {
        setIsHovered(true);
        playSound(soundType);
      }}
      onHoverEnd={() => setIsHovered(false)}
      onClick={() => {
        playSound('magic');
        onClick?.();
      }}
      className={`cursor-pointer ${className}`}
    >
      {children}
    </motion.div>
  );
}

// Capability data - Teal/Green/Blue theme
const capabilities = [
  {
    icon: Video,
    title: "AI Video",
    subtitle: "Kling AI",
    description: "Cinematic quality videos from text. Product demos, social content, ads.",
    price: "$5",
    gradient: "from-teal-500 to-cyan-500",
    glow: "teal",
    hoverSound: "whoosh",
  },
  {
    icon: Image,
    title: "AI Images",
    subtitle: "GPT Image 1",
    description: "Stunning visuals, logos, marketing graphics. Commercial-ready.",
    price: "$2",
    gradient: "from-green-500 to-emerald-500",
    glow: "green",
    hoverSound: "pop",
  },
  {
    icon: Mic,
    title: "AI Voice",
    subtitle: "ElevenLabs",
    description: "Natural voiceovers, narration, podcasts. Studio quality.",
    price: "$1",
    gradient: "from-blue-600 to-indigo-600",
    glow: "blue",
    hoverSound: "magic",
  },
  {
    icon: FileText,
    title: "AI Content",
    subtitle: "7 Specialists",
    description: "Blogs, SEO, legal docs, code reviews. Expert-level output.",
    price: "From $0.50",
    gradient: "from-cyan-500 to-teal-500",
    glow: "cyan",
    hoverSound: "success",
  },
];

// Agent roster
const agents = [
  { name: "ContentBot", specialty: "Blog & Articles", price: "$0.50", icon: FileText },
  { name: "VideoGen", specialty: "AI Video Creation", price: "$5", icon: Video },
  { name: "ImageGen", specialty: "Graphics & Photos", price: "$2", icon: Image },
  { name: "VoiceGen", specialty: "Voice & Narration", price: "$1", icon: Mic },
  { name: "LegalDraft", specialty: "Contracts & NDAs", price: "$0.75", icon: Shield },
  { name: "CodeGuard", specialty: "Code + Execution", price: "$0.75", icon: Code },
  { name: "DataMind", specialty: "Data Analysis", price: "$0.50", icon: Database },
  { name: "ResearchBot", specialty: "Live Web Search", price: "$0.50", icon: Globe },
  { name: "ScriptGen", specialty: "Video Scripts", price: "$0.50", icon: Play },
  { name: "SEOWriter", specialty: "SEO Content", price: "$0.50", icon: Brain },
];

// Platform features - UPGRADED with all new capabilities
const features = [
  { icon: Bot, title: "Custom Chatbots", description: "Upload your docs, get an AI chatbot. Embed on your website!", highlight: true },
  { icon: Globe, title: "Live Web Search", description: "Real-time internet access. Get today's prices, news, and data." },
  { icon: Code, title: "Code Execution", description: "Actually run your code in a secure sandbox. See real output." },
  { icon: Image, title: "Image Analysis", description: "Upload images and ask questions. GPT-4o Vision built-in." },
  { icon: FileText, title: "PDF & Doc Parsing", description: "Upload PDFs, extract text and tables automatically." },
  { icon: Mic, title: "Voice Conversations", description: "Speak to agents. Get voice responses. Real-time chat." },
  { icon: MessagesSquare, title: "Memory & Context", description: "Agents remember your project details across conversations." },
  { icon: Upload, title: "File Uploads", description: "Images, PDFs, code, data files. All analyzed instantly." },
  { icon: Lock, title: "Commercial License", description: "Full ownership. Use everything for business. No royalties." },
];

export default function LandingPage() {
  const navigate = useNavigate();
  const { playSound, soundEnabled, toggleSound } = useSound();
  const { t } = useTranslation();
  const heroRef = useRef(null);
  const [showSecondLine, setShowSecondLine] = useState(false);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"]
  });
  
  const heroOpacity = useTransform(scrollYProgress, [0, 1], [1, 0]);
  const heroScale = useTransform(scrollYProgress, [0, 1], [1, 0.8]);
  const heroY = useTransform(scrollYProgress, [0, 1], [0, 100]);

  const handleNavigate = (path) => {
    playSound('whoosh');
    navigate(path);
  };

  return (
    <div className="min-h-screen overflow-hidden" data-testid="landing-page">
      {/* Tommy's animated background - Time-of-day aware! */}
      <TommyBackground useTimeOfDay={true} opacity={0.12} />
      
      {/* Sound toggle */}
      <motion.button
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1 }}
        onClick={() => {
          playSound('pop');
          toggleSound();
        }}
        className="fixed top-24 right-6 z-50 w-10 h-10 rounded-full bg-zinc-900/80 border border-zinc-700 flex items-center justify-center hover:border-teal-500/50 transition-colors"
        data-testid="sound-toggle"
      >
        {soundEnabled ? (
          <Volume2 className="w-4 h-4 text-teal-400" />
        ) : (
          <VolumeX className="w-4 h-4 text-zinc-500" />
        )}
      </motion.button>

      {/* HERO SECTION */}
      <section ref={heroRef} className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <Particles />
        
        {/* Gradient orbs - Teal/Green/Blue */}
        <div className="absolute top-1/4 left-1/4 w-[600px] h-[600px] bg-teal-500/10 rounded-full blur-[120px] animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-[500px] h-[500px] bg-blue-600/10 rounded-full blur-[100px] animate-pulse" style={{ animationDelay: '1s' }} />
        <div className="absolute top-1/2 right-1/3 w-[400px] h-[400px] bg-green-500/10 rounded-full blur-[100px] animate-pulse" style={{ animationDelay: '0.5s' }} />
        
        <motion.div 
          style={{ opacity: heroOpacity, scale: heroScale, y: heroY }}
          className="relative z-10 max-w-7xl mx-auto px-6 py-32"
        >
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left - Text */}
            <motion.div
              initial={{ opacity: 0, x: -60 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            >
              {/* Badge */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-orange-500/10 to-amber-500/10 border border-orange-500/20 mb-8"
              >
                <div className="w-2 h-2 rounded-full bg-orange-400 animate-pulse" />
                <span className="text-sm font-medium bg-gradient-to-r from-orange-300 to-amber-300 bg-clip-text text-transparent">
                  {t('landing.badge')}
                </span>
              </motion.div>

              {/* Headline with TYPEWRITER EFFECT */}
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black leading-[0.95] tracking-tight mb-8">
                <motion.span 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.3 }}
                  className="block text-white"
                >
                  <TypewriterText 
                    text="They Said AI" 
                    delay={500}
                    onComplete={() => setShowSecondLine(true)}
                  />
                </motion.span>
                <span className="block mt-2 min-h-[1.2em]">
                  {showSecondLine && (
                    <span className="bg-gradient-to-r from-teal-400 via-green-400 to-emerald-400 bg-clip-text text-transparent">
                      <TypewriterText 
                        text="Was Only for Big Companies." 
                        delay={0}
                      />
                    </span>
                  )}
                </span>
              </h1>

              {/* Subheadline */}
              <motion.p 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 2.5 }}
                className="text-xl text-zinc-400 mb-10 max-w-lg leading-relaxed"
              >
                <span className="text-white font-semibold">We opened the vault.</span> 10 AI agents that search the web, write code, create videos, and build apps.
                <span className="text-orange-400 font-semibold"> Your first 5 tasks are free.</span>
              </motion.p>

              {/* CTA Buttons */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 2.7 }}
                className="flex flex-col sm:flex-row gap-4"
              >
                <Button
                  size="lg"
                  onClick={() => handleNavigate('/templates')}
                  className="group relative overflow-hidden bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-400 hover:to-amber-400 text-white font-bold rounded-full px-8 py-7 text-lg transition-all duration-300 shadow-[0_0_40px_rgba(249,115,22,0.4)] hover:shadow-[0_0_60px_rgba(249,115,22,0.6)]"
                  data-testid="hero-cta-free-tasks"
                >
                  <span className="relative z-10 flex items-center">
                    {t('landing.cta1')}
                    <Gift className="ml-2 w-5 h-5 group-hover:scale-110 transition-transform" />
                  </span>
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  onClick={() => handleNavigate('/agents')}
                  className="border-zinc-700 hover:border-teal-500/50 text-white hover:text-teal-300 rounded-full px-8 py-7 text-lg hover:bg-teal-500/5 transition-all"
                  data-testid="hero-cta-primary"
                >
                  <span className="flex items-center">
                    {t('landing.cta2')}
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </span>
                </Button>
              </motion.div>

              {/* Trust badges */}
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 2.9 }}
                className="mt-10 flex items-center gap-6 text-sm text-zinc-500"
              >
                <div className="flex items-center gap-2">
                  <Gift className="w-4 h-4 text-orange-500" />
                  <span>5 free tasks</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-500" />
                  <span>No credit card</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-500" />
                  <span>Results in seconds</span>
                </div>
              </motion.div>

              {/* Social Proof Counter */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 3.1 }}
                className="mt-6 flex items-center gap-3"
              >
                <div className="flex -space-x-2">
                  {['🧑‍💼', '👩‍💻', '👨‍🎨', '👩‍🔬'].map((emoji, i) => (
                    <div key={i} className="w-8 h-8 rounded-full bg-zinc-800 border-2 border-zinc-950 flex items-center justify-center text-sm">
                      {emoji}
                    </div>
                  ))}
                </div>
                <div className="text-sm">
                  <span className="text-white font-semibold">50+ creators</span>
                  <span className="text-zinc-500"> are building with TripleTC</span>
                </div>
              </motion.div>
            </motion.div>

            {/* Right - Tommy Hero */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.4, duration: 1, ease: [0.22, 1, 0.36, 1] }}
              className="hidden lg:block relative"
            >
              <div className="relative">
                {/* Glow effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-teal-500/20 to-blue-500/20 rounded-3xl blur-3xl" />
                
                {/* Main image container - cleaner, no floating badges */}
                <motion.div 
                  className="relative rounded-3xl overflow-hidden border border-teal-500/20"
                  whileHover={{ scale: 1.02 }}
                  transition={{ duration: 0.3 }}
                >
                  <img 
                    src={TOMMY_HERO} 
                    alt="Tommy The Tuxedo Cat - AI Agent Commander"
                    className="w-full h-auto object-cover"
                  />
                  
                  {/* Overlay gradient */}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#030303] via-transparent to-transparent" />
                  
                  {/* Bottom card overlay */}
                  <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/90 to-transparent">
                    <div className="flex items-center gap-3">
                      <div className="w-3 h-3 rounded-full bg-green-400 animate-pulse" />
                      <span className="text-white font-semibold">10 Agents Ready</span>
                    </div>
                    <p className="text-zinc-400 text-sm mt-1">Video • Images • Voice • Code • Web Search</p>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </motion.div>

        {/* Scroll indicator */}
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 3 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
        >
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
            className="w-6 h-10 rounded-full border-2 border-zinc-600 flex justify-center pt-2"
          >
            <motion.div 
              animate={{ opacity: [0.5, 1, 0.5] }}
              transition={{ duration: 1.5, repeat: Infinity }}
              className="w-1 h-2 rounded-full bg-teal-400" 
            />
          </motion.div>
        </motion.div>
      </section>

      {/* CAPABILITIES SECTION - WITH HOVER SOUNDS */}
      <section id="capabilities" className="relative py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#030303] via-zinc-950 to-[#030303]" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-6">
          {/* Section header */}
          <motion.div 
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-20"
          >
            <span className="text-teal-400 text-sm font-bold tracking-widest uppercase">
              What We Create
            </span>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-white mt-4 mb-6">
              Four Types of Magic
            </h2>
            <p className="text-xl text-zinc-400 max-w-2xl mx-auto">
              Not just chatbots. Real creative work, delivered.
            </p>
          </motion.div>

          {/* Capability cards - WITH UNIQUE HOVER SOUNDS */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {capabilities.map((cap, i) => (
              <InteractiveCard
                key={cap.title}
                delay={i * 0.1}
                onClick={() => handleNavigate('/agents')}
                soundType={cap.hoverSound}
                className={`group relative bg-zinc-900/50 backdrop-blur-sm border border-zinc-800 rounded-3xl p-8 overflow-hidden transition-all duration-500 hover:border-${cap.glow}-500/50`}
              >
                {/* Hover glow */}
                <div className={`absolute inset-0 bg-gradient-to-br ${cap.gradient} opacity-0 group-hover:opacity-10 transition-opacity duration-500`} />
                
                {/* Animated ring on hover */}
                <motion.div 
                  className={`absolute inset-0 rounded-3xl border-2 border-transparent group-hover:border-${cap.glow}-500/30`}
                  initial={false}
                  animate={{ scale: [1, 1.02, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                />
                
                {/* Icon with pulse effect */}
                <motion.div 
                  className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${cap.gradient} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}
                  whileHover={{ rotate: [0, -5, 5, 0] }}
                  transition={{ duration: 0.5 }}
                >
                  <cap.icon className="w-8 h-8 text-white" />
                </motion.div>
                
                {/* Content */}
                <h3 className="text-2xl font-bold text-white mb-1 group-hover:text-teal-300 transition-colors">{cap.title}</h3>
                <p className="text-sm text-zinc-500 mb-4">{cap.subtitle}</p>
                <p className="text-zinc-400 text-sm leading-relaxed mb-6">{cap.description}</p>
                
                {/* Price with glow on hover */}
                <div className="flex items-center justify-between">
                  <motion.span 
                    className="text-3xl font-black text-white group-hover:text-teal-400 transition-colors"
                    whileHover={{ scale: 1.05 }}
                  >
                    {cap.price}
                  </motion.span>
                  <ArrowRight className="w-5 h-5 text-zinc-600 group-hover:text-white group-hover:translate-x-1 transition-all" />
                </div>
              </InteractiveCard>
            ))}
          </div>
        </div>
      </section>

      {/* WHY TRIPLETC SECTION */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-teal-500/5 rounded-full blur-[150px]" />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left - Tommy */}
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="relative max-w-md mx-auto">
                <motion.img 
                  src={TOMMY_MASCOT}
                  alt="Tommy"
                  className="w-full rounded-3xl border border-teal-500/20"
                  whileHover={{ scale: 1.02 }}
                  transition={{ duration: 0.3 }}
                />
                <div className="absolute inset-0 rounded-3xl bg-gradient-to-t from-[#030303] via-transparent to-transparent" />
              </div>
            </motion.div>

            {/* Right - Content */}
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-teal-400 text-sm font-bold tracking-widest uppercase">
                Why TripleTC
              </span>
              <h2 className="text-4xl md:text-5xl font-black text-white mt-4 mb-8">
                They Chat.<br />
                <span className="bg-gradient-to-r from-teal-400 to-green-400 bg-clip-text text-transparent">
                  We Deliver.
                </span>
              </h2>
              
              <div className="space-y-6">
                {[
                  { title: "Real Work Products", desc: "Not conversations — actual videos, images, documents, and code ready to use." },
                  { title: "Live Internet Access", desc: "ResearchBot searches the web in real-time. Get today's data, not yesterday's." },
                  { title: "Runs Your Code", desc: "CodeGuard doesn't just review — it executes code in a secure sandbox." },
                  { title: "Understands Images", desc: "Upload screenshots, diagrams, photos. Our agents see and analyze them." },
                  { title: "Voice Conversations", desc: "Speak naturally. Get voice responses. Like talking to a real team member." },
                  { title: "Pay Per Task", desc: "No subscriptions. No monthly fees. Pay only when you need work done." },
                ].map((item, i) => (
                  <motion.div
                    key={item.title}
                    initial={{ opacity: 0, x: 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                    className="flex gap-4"
                  >
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-teal-500/10 flex items-center justify-center">
                      <Check className="w-5 h-5 text-teal-400" />
                    </div>
                    <div>
                      <h4 className="text-lg font-bold text-white">{item.title}</h4>
                      <p className="text-zinc-400">{item.desc}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* AGENTS ROSTER */}
      <section className="py-24 bg-zinc-950/80">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div 
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-green-400 text-sm font-bold tracking-widest uppercase">
              The Roster
            </span>
            <h2 className="text-4xl md:text-5xl font-black text-white mt-4 mb-6">
              10 AI Agents. Ready Now.
            </h2>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {agents.map((agent, i) => (
              <InteractiveCard
                key={agent.name}
                delay={i * 0.05}
                onClick={() => handleNavigate('/agents')}
                soundType="pop"
                className="group bg-zinc-900/50 border border-zinc-800 rounded-2xl p-5 text-center hover:border-teal-500/50 transition-all"
              >
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-teal-500/20 to-blue-500/20 flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform">
                  <agent.icon className="w-6 h-6 text-teal-400" />
                </div>
                <h4 className="font-bold text-white group-hover:text-teal-400 transition-colors">{agent.name}</h4>
                <p className="text-xs text-zinc-500 mb-2">{agent.specialty}</p>
                <span className="text-lg font-bold text-teal-400">{agent.price}</span>
              </InteractiveCard>
            ))}
          </div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <Button
              size="lg"
              onClick={() => handleNavigate('/agents')}
              className="bg-gradient-to-r from-teal-500 to-blue-600 hover:from-teal-400 hover:to-blue-500 text-white font-bold rounded-full px-10 py-7 text-lg shadow-[0_0_30px_rgba(20,184,166,0.3)] hover:shadow-[0_0_50px_rgba(20,184,166,0.5)] transition-all"
              data-testid="agents-cta"
            >
              Browse All Agents
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* FEATURES GRID */}
      <section className="py-24 bg-zinc-950/60">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div 
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-blue-400 text-sm font-bold tracking-widest uppercase">
              Everything You Need
            </span>
            <h2 className="text-4xl md:text-5xl font-black text-white mt-4 mb-4">
              Powerful Features, Zero Complexity
            </h2>
            <p className="text-xl text-zinc-400 max-w-2xl mx-auto">
              Web search. Code execution. Image analysis. Voice chat. All in one platform.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, i) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                onClick={() => feature.highlight && handleNavigate('/chatbot-builder')}
                className={`group bg-zinc-900/30 border rounded-2xl p-8 transition-all ${
                  feature.highlight 
                    ? 'border-teal-500/50 hover:border-teal-400 cursor-pointer ring-1 ring-teal-500/20' 
                    : 'border-zinc-800/50 hover:border-blue-500/30'
                }`}
              >
                <div className={`w-14 h-14 rounded-xl flex items-center justify-center mb-5 group-hover:scale-110 transition-transform ${
                  feature.highlight ? 'bg-teal-500/20' : 'bg-blue-500/10'
                }`}>
                  <feature.icon className={`w-7 h-7 ${feature.highlight ? 'text-teal-400' : 'text-blue-400'}`} />
                </div>
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="text-xl font-bold text-white">{feature.title}</h3>
                  {feature.highlight && (
                    <span className="text-[10px] font-bold bg-teal-500/20 text-teal-400 px-2 py-0.5 rounded-full">NEW</span>
                  )}
                </div>
                <p className="text-zinc-500">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative py-32 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-teal-500/10 rounded-full blur-[150px]" />
          <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-blue-600/10 rounded-full blur-[120px]" />
        </div>
        
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
          >
            <motion.img 
              src={TOMMY_MASCOT}
              alt="Tommy"
              className="w-32 h-32 rounded-full mx-auto mb-8 border-4 border-teal-500/30"
              animate={{
                boxShadow: [
                  '0 0 30px rgba(20, 184, 166, 0.3)',
                  '0 0 60px rgba(20, 184, 166, 0.5)',
                  '0 0 30px rgba(20, 184, 166, 0.3)',
                ],
              }}
              transition={{ duration: 2, repeat: Infinity }}
            />
            
            <h2 className="text-4xl md:text-6xl font-black text-white mb-6">
              Ready to Create<br />
              <span className="bg-gradient-to-r from-teal-400 via-green-400 to-emerald-400 bg-clip-text text-transparent">
                Something Amazing?
              </span>
            </h2>
            
            <p className="text-xl text-zinc-400 mb-10 max-w-2xl mx-auto">
              Web search, code execution, image analysis, voice chat, video generation — all from one platform.
              <br />
              <span className="text-white font-semibold">No subscriptions. Just results.</span>
            </p>

            <Button
              size="lg"
              onClick={() => handleNavigate('/agents')}
              className="bg-white hover:bg-zinc-100 text-black font-bold rounded-full px-12 py-8 text-xl shadow-[0_0_50px_rgba(255,255,255,0.2)] hover:shadow-[0_0_80px_rgba(255,255,255,0.3)] transition-all"
              data-testid="final-cta"
            >
              Get Started Free
              <ArrowRight className="ml-3 w-6 h-6" />
            </Button>

            <p className="text-zinc-600 mt-8 text-sm">
              No credit card required • Browse without signing up • Pay only for results
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
